import { useEffect, useRef, useState } from "react";
import {
  ActivityIndicator,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";
import { prepareBrowserVoiceClient } from "../lib/webVoiceClient";

type Status =
  | "idle"
  | "connecting"
  | "connected"
  | "disconnected"
  | "error";

type VoiceSession = Awaited<
  ReturnType<
    Awaited<ReturnType<typeof prepareBrowserVoiceClient>>["connect"]
  >
>;

type PreparedClient = Awaited<ReturnType<typeof prepareBrowserVoiceClient>>;

export function DevMockCalleeScreen() {
  const [sessionId, setSessionId] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState<string>("");

  const preparedRef = useRef<PreparedClient | null>(null);
  const sessionRef = useRef<VoiceSession | null>(null);

  useEffect(() => {
    return () => {
      sessionRef.current?.destroy();
      preparedRef.current?.destroy();
    };
  }, []);

  async function join() {
    if (!sessionId.trim()) {
      setMessage("Paste a session ID first.");
      return;
    }
    setStatus("connecting");
    setMessage("");
    try {
      sessionRef.current?.destroy();
      preparedRef.current?.destroy();
      preparedRef.current = await prepareBrowserVoiceClient({
        userId: `dev-callee-${Date.now()}`,
        identity: "dev-callee",
        leg: "pstn",
        handlers: {
          onDisconnected: () => {
            sessionRef.current = null;
            setStatus("disconnected");
          },
          onError: (msg) => {
            setStatus("error");
            setMessage(msg);
          },
          onStatusChanged: (s) => {
            setMessage(`leg status: ${s}`);
          },
        },
      });
      sessionRef.current = await preparedRef.current.connect(sessionId.trim());
      setStatus("connected");
      setMessage("Connected as callee. Speak into your mic.");
    } catch (err) {
      setStatus("error");
      setMessage(err instanceof Error ? err.message : String(err));
    }
  }

  function hangUp() {
    sessionRef.current?.destroy();
    sessionRef.current = null;
    preparedRef.current?.destroy();
    preparedRef.current = null;
    setStatus("idle");
    setMessage("Disconnected.");
  }

  return (
    <View style={styles.root}>
      <Text style={styles.title}>Dev: Join as Callee</Text>
      <Text style={styles.subtitle}>
        Paste a session ID from the caller tab. This opens the pstn leg WebSocket
        using your browser mic/speakers. No Twilio cost.
      </Text>
      <TextInput
        value={sessionId}
        onChangeText={setSessionId}
        placeholder="session id (from caller tab)"
        placeholderTextColor="#888"
        autoCapitalize="none"
        autoCorrect={false}
        style={styles.input}
        editable={status !== "connecting" && status !== "connected"}
      />
      <View style={styles.row}>
        {status === "connected" ? (
          <Pressable onPress={hangUp} style={[styles.btn, styles.danger]}>
            <Text style={styles.btnText}>Hang up</Text>
          </Pressable>
        ) : (
          <Pressable
            onPress={join}
            style={[styles.btn, status === "connecting" && styles.btnDisabled]}
            disabled={status === "connecting"}
          >
            {status === "connecting" ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <Text style={styles.btnText}>Join as callee</Text>
            )}
          </Pressable>
        )}
      </View>
      <Text style={styles.status}>status: {status}</Text>
      {message ? <Text style={styles.msg}>{message}</Text> : null}
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: "#111",
    padding: 24,
    gap: 16,
  },
  title: { color: "#fff", fontSize: 22, fontWeight: "600", marginTop: 32 },
  subtitle: { color: "#aaa", fontSize: 14, lineHeight: 20 },
  input: {
    backgroundColor: "#222",
    color: "#fff",
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    fontFamily: Platform.OS === "web" ? "monospace" : undefined,
  },
  row: { flexDirection: "row", gap: 12 },
  btn: {
    backgroundColor: "#3478f6",
    paddingHorizontal: 20,
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: "center",
    minWidth: 180,
  },
  btnDisabled: { opacity: 0.6 },
  danger: { backgroundColor: "#d93b3b" },
  btnText: { color: "#fff", fontSize: 16, fontWeight: "600" },
  status: { color: "#fff", fontSize: 14 },
  msg: { color: "#ffcb4b", fontSize: 13, lineHeight: 18 },
});
